import { Amplify } from "aws-amplify";
import { CookieStorage, Hub, parseAmplifyConfig } from "aws-amplify/utils";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { autoSignIn, confirmSignUp, confirmUserAttribute, fetchAuthSession, fetchUserAttributes as auth_fetchUserAttributes, getCurrentUser, resendSignUpCode, sendUserAttributeVerificationCode, signIn as auth_signIn, signInWithRedirect, signOut as auth_signOut, signUp as auth_signUp } from "aws-amplify/auth";
import { getAwsSettings } from "./awsSettings.mjs";
var auth_cognito_legacyIdTokenPayloadKeys = /*#__PURE__*/ function(legacyIdTokenPayloadKeys) {
    legacyIdTokenPayloadKeys["address"] = "address";
    legacyIdTokenPayloadKeys["addressFormatted"] = "formatted";
    legacyIdTokenPayloadKeys["ansiraId"] = "custom:ansiraId";
    legacyIdTokenPayloadKeys["ansiraUuid"] = "custom:ansiraUuid";
    legacyIdTokenPayloadKeys["authTime"] = "auth_time";
    legacyIdTokenPayloadKeys["cognitoUsername"] = "cognito:username";
    legacyIdTokenPayloadKeys["purinaAdsOptIn"] = "custom:purinaAdsOptIn";
    legacyIdTokenPayloadKeys["termsAndConditions"] = "custom:tAndC";
    legacyIdTokenPayloadKeys["smsTransactionTAndC"] = "custom:smsTransactionTAndC";
    legacyIdTokenPayloadKeys["email"] = "email";
    legacyIdTokenPayloadKeys["emailVerified"] = "email_verified";
    legacyIdTokenPayloadKeys["eventId"] = "event_id";
    legacyIdTokenPayloadKeys["familyName"] = "family_name";
    legacyIdTokenPayloadKeys["givenName"] = "given_name";
    legacyIdTokenPayloadKeys["name"] = "name";
    legacyIdTokenPayloadKeys["sub"] = "sub";
    legacyIdTokenPayloadKeys["phoneNumber"] = "phone_number";
    legacyIdTokenPayloadKeys["phoneNumberVerified"] = "phone_number_verified";
    legacyIdTokenPayloadKeys["tokenUse"] = "token_use";
    return legacyIdTokenPayloadKeys;
}({});
var auth_cognito_legacyAccessTokenPayloadKeys = /*#__PURE__*/ function(legacyAccessTokenPayloadKeys) {
    legacyAccessTokenPayloadKeys["ansiraId"] = "ansira_id";
    legacyAccessTokenPayloadKeys["ansiraUuid"] = "ansira_uuid";
    legacyAccessTokenPayloadKeys["tokenUse"] = "token_use";
    legacyAccessTokenPayloadKeys["username"] = "username";
    return legacyAccessTokenPayloadKeys;
}({});
const profileAuth = (config, ssr = false)=>{
    const cognitoConfig = config.cognito;
    const pupConfig = config.pup;
    const analyticsConfig = config.analytics;
    const isClientSide = !!('undefined' != typeof window && window.location);
    const queryString = isClientSide ? window.location.search : '';
    const urlParams = new URLSearchParams(queryString);
    if (urlParams.get('error') && urlParams.get("error_description")?.includes('PreSignUp')) {
        const preSignUpError = urlParams.get("error_description");
        urlParams.delete('error');
        urlParams.delete("error_description");
        const origin = `${window.location.protocol}//${window.location.host}${window.location.pathname}${urlParams.size ? `?${urlParams.toString()}` : ''}`;
        const redirectParams = new URLSearchParams({
            origin: encodeURIComponent(origin),
            client: cognitoConfig.userPoolWebClientId,
            preSignUpError
        });
        if (pupConfig.property) redirectParams.append('property', pupConfig.property);
        if (pupConfig.method) redirectParams.append('method', pupConfig.method);
        if (analyticsConfig?.signUpSourceCode) redirectParams.append('sign-up-source', analyticsConfig.signUpSourceCode);
        const signUpUrl = `${pupConfig.url}${pupConfig.url.endsWith('/') ? '' : '/'}sign-up`;
        window.location.href = `${signUpUrl}?${redirectParams.toString()}`;
    } else {
        Amplify.configure(parseAmplifyConfig({
            ...getAwsSettings(cognitoConfig)
        }), {
            ssr
        });
        if (cognitoConfig.cookieStorage) cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieStorage({
            domain: cognitoConfig.cookieStorage.domain,
            expires: cognitoConfig.cookieStorage.expires,
            path: cognitoConfig.cookieStorage.path,
            secure: cognitoConfig.cookieStorage.domain.includes('localhost') ? false : cognitoConfig.cookieStorage.secure,
            sameSite: cognitoConfig.cookieStorage.sameSite || 'lax'
        }));
    }
    const fetchUserAttributes = async ()=>{
        const user = await getCurrentUser();
        const session = await fetchAuthSession();
        const userAttrs = await auth_fetchUserAttributes();
        return {
            id: user ? session.identityId : void 0,
            username: user.username,
            attributes: userAttrs
        };
    };
    const getSession = async ()=>{
        const session = await fetchAuthSession();
        return {
            accessToken: session.tokens?.accessToken.toString(),
            accessTokenAttributes: session.tokens?.accessToken.payload,
            idToken: session.tokens?.idToken?.toString(),
            idTokenAttributes: session.tokens?.idToken?.payload,
            rawSession: session
        };
    };
    const confirmCodeSignUp = async (username, confirmationCode, options)=>confirmSignUp({
            username,
            confirmationCode,
            options
        });
    const refreshUser = async ()=>{
        const responses = await Promise.all([
            fetchAuthSession({
                forceRefresh: true
            }),
            getCurrentUser()
        ]);
        return {
            ...responses[0],
            ...responses[1]
        };
    };
    const signIn = async (opts)=>await auth_signIn(opts);
    const federatedSignIn = async (opts)=>await signInWithRedirect(opts);
    const signUp = async (opts)=>await auth_signUp(opts);
    const signOut = async (opts)=>await auth_signOut(opts);
    const resendConfirmationCode = async (emailAddress)=>await resendSignUpCode({
            username: emailAddress
        });
    const resendSmsCode = async ()=>await sendUserAttributeVerificationCode({
            userAttributeKey: 'phone_number'
        });
    const verifyUserAttributeWithCode = async (attr, verificationCode)=>await confirmUserAttribute({
            userAttributeKey: attr,
            confirmationCode: verificationCode
        });
    return {
        autoSignIn: autoSignIn,
        signIn,
        federatedSignIn,
        confirmCodeSignUp,
        fetchUserAttributes,
        getSession,
        refreshUser,
        signUp,
        signOut,
        resendConfirmationCode,
        resendSmsCode,
        verifyUserAttributeWithCode,
        Hub: Hub
    };
};
function getTokenFromHeaders(headers, tokenType, clientId, decode = true) {
    const cookies = headers.get('cookie');
    const cognitoCookie = cookies?.split(';').find((cookie)=>cookie.trim().startsWith(`CognitoIdentityServiceProvider.${clientId}`) && cookie.trim().includes(`${tokenType}Token`));
    if (!decode) return cognitoCookie?.split('=')[1];
    const token = cognitoCookie?.split('=')[1].split('.')[1];
    const decodedToken = token && JSON.parse(Buffer.from(token, 'base64').toString('utf8'));
    if (!decodedToken) return;
    return decodedToken;
}
function getTokenFromCookies(tokenType, clientId) {
    if ('undefined' == typeof document) return null;
    const cookies = document.cookie;
    const cognitoCookie = cookies?.split(';').find((cookie)=>cookie.trim().startsWith(`CognitoIdentityServiceProvider.${clientId}`) && cookie.trim().includes(`${tokenType}Token`));
    const token = cognitoCookie?.split('=')[1].split('.')[1];
    const decodedToken = token && JSON.parse(Buffer.from(token, 'base64').toString('utf8'));
    if (!decodedToken) return;
    return decodedToken;
}
const userValidations = {
    hasZipcode: (_, user)=>user?.address?.postalCode && user.address?.postalCode !== '00000',
    hasTandC: (cognitoUserAttributes)=>cognitoUserAttributes?.['custom:tAndC']?.toLowerCase() === 'true'
};
export { getTokenFromCookies, getTokenFromHeaders, auth_cognito_legacyAccessTokenPayloadKeys as legacyAccessTokenPayloadKeys, auth_cognito_legacyIdTokenPayloadKeys as legacyIdTokenPayloadKeys, profileAuth, userValidations };
